import { create } from "zustand"
import { useConfirmStore } from "@/stores/useConfirmStore"

type OpenDrawerOptions = {
  onBeforeOpen?: () => void
}

type CloseDrawerOptions = {
  onConfirm?: () => void
}

type State = {
  isDirty: boolean
  drawerName: string | null
  onDrawerClose?: () => void
  DrawerContent: ((props: { onDirty?(state: boolean): void }) => React.JSX.Element) | null
}

type Actions = {
  openDrawer: (
    component: (props: { onDirty?(state: boolean): void }) => React.JSX.Element,
    name?: string,
    options?: OpenDrawerOptions,
  ) => void
  openDrawerWithoutConfirmation: (
    component: (props: { onDirty?(state: boolean): void }) => React.JSX.Element,
    name?: string,
    options?: OpenDrawerOptions,
  ) => void
  setDirty: (isDirty: boolean) => void
  setOnDrawerClose: (onDrawerClose: () => void) => void
  closeDrawer: () => void
  closeDrawerWithExclude: (drawerNames: string[]) => void
  closeDrawerWithConfirmation: (options?: CloseDrawerOptions) => void
}

const { confirm } = useConfirmStore.getState()

const initialState: State = {
  drawerName: null,
  DrawerContent: null,
  isDirty: false,
}

const useDrawerStore = create<State & Actions>((set, get) => ({
  ...initialState,

  setDirty: (isDirty: boolean) => {
    set({
      isDirty,
    })
  },

  setOnDrawerClose: (onDrawerClose) => {
    set({ onDrawerClose })
  },

  openDrawer: async (component, name, options) => {
    if (get().isDirty) {
      const confirmed = await confirm({})
      if (!confirmed) return
    }

    if (options?.onBeforeOpen) options.onBeforeOpen()

    set(() => {
      return {
        isDirty: false,
        DrawerContent: component,
        drawerName: name,
      }
    })
  },
  openDrawerWithoutConfirmation: (component, name, options) => {
    if (options?.onBeforeOpen) options.onBeforeOpen()

    set(() => {
      return {
        isDirty: false,
        DrawerContent: component,
        drawerName: name,
      }
    })
  },

  closeDrawer: () => {
    set(initialState)
  },

  closeDrawerWithExclude: (drawerNames: string[]) => {
    const { drawerName } = get()
    if (drawerName && drawerNames.includes(drawerName)) return
    get().closeDrawer()
  },

  closeDrawerWithConfirmation: async (options) => {
    if (get().isDirty) {
      const confirmed = await confirm({})
      if (!confirmed) return
    }
    get().closeDrawer()
    options?.onConfirm?.()
  },
}))

const getIsDirty = () => useDrawerStore.getState().isDirty

export { useDrawerStore, getIsDirty }
